import { Helmet } from 'react-helmet';
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Pencil, Trash2, Plus, X, Power, PowerOff } from 'lucide-react';
import { getAdminPlans, addPlan, updatePlan, deletePlan, togglePlanStatus } from '../../lib/apiService';

const PlansPage = () => {
  const { userProfile } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  
  // Form fields
  const [form, setForm] = useState({
    plan_name: '',
    monthly_amount: '',
    yearly_amount: '',
    monthly_chats: '',
    monthly_searches: '',
    monthly_property_add: ''
  });

  const fetchPlans = useCallback(async () => {
    if (!userProfile) return;
    try {
      const res = await getAdminPlans();
      setPlans(res?.plans || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to fetch plans');
    } finally {
      setLoading(false);
    }
  }, [userProfile]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const openAddModal = () => {
    setEditingPlan(null);
    setForm({
      plan_name: '',
      monthly_amount: '',
      yearly_amount: '',
      monthly_chats: '',
      monthly_searches: '',
      monthly_property_add: ''
    });
    setIsModalOpen(true);
  };

  const openEditModal = (plan) => {
    setEditingPlan(plan);
    setForm({
      plan_name: plan.plan_name || '',
      monthly_amount: plan.monthly_amount || 0,
      yearly_amount: plan.yearly_amount || 0,
      monthly_chats: plan.monthly_chats || 0,
      monthly_searches: plan.monthly_searches || 0,
      monthly_property_add: plan.monthly_property_add || 0
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this plan permanently?')) return;
    try {
      const res = await deletePlan(id);
      if (res.status) {
        toast.success(res.message || 'Plan deleted');
        fetchPlans();
      } else {
        toast.error(res.message || 'Failed to delete plan');
      }
    } catch (err) {
      toast.error('Failed to delete plan');
    }
  };

  const handleToggleStatus = async (plan) => {
    const newStatus = plan.is_active === 'true' ? 'false' : 'true';
    if (!window.confirm(`Are you sure you want to ${newStatus === 'true' ? 'activate' : 'deactivate'} this plan?`)) return;
    try {
      const res = await togglePlanStatus(plan.id, newStatus);
      if (res.status) {
        toast.success(`Plan ${newStatus === 'true' ? 'activated' : 'deactivated'}`);
        fetchPlans();
      } else {
        toast.error(res.message || 'Failed to update plan status');
      }
    } catch (err) {
      toast.error('Failed to update plan status');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.plan_name) return toast.error('Plan name is required');
    
    setIsSubmitting(true);
    try {
      let res;
      if (editingPlan) {
        res = await updatePlan(editingPlan.id, form);
      } else {
        res = await addPlan(form);
      }

      if (res.status) {
        toast.success(res.message || `Plan ${editingPlan ? 'updated' : 'created'}`);
        setIsModalOpen(false);
        fetchPlans();
      } else {
        toast.error(res.message || `Failed to ${editingPlan ? 'update' : 'create'} plan`);
      }
    } catch (err) {
      toast.error('An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-8 h-8 border-4 border-[#ee7923] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      <Helmet>
        <title>Plans Management | JoldiPabo Admin</title>
      </Helmet>
      
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Plans Management
          </h1>
          <p className="text-sm text-slate-600 mt-1">Create and manage subscription plans, quotas and pricing</p>
        </div>
        <Button onClick={openAddModal} className="gap-2 bg-slate-900 hover:bg-slate-800">
          <Plus className="w-4 h-4" /> Add Plan
        </Button>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr className="border-b border-slate-200">
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Plan Name</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Pricing (Mon/Yr)</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Quotas</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody>
              {plans.map(p => (
                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4">
                    <span className="text-sm font-medium text-slate-900">{p.plan_name}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-semibold text-slate-900">₹{p.monthly_amount} <span className="text-xs font-normal text-slate-500">/ mo</span></span>
                      <span className="text-sm font-semibold text-slate-900">₹{p.yearly_amount} <span className="text-xs font-normal text-slate-500">/ yr</span></span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-xs text-slate-600 space-y-1">
                      <p>Chats: <span className="font-semibold text-slate-900">{p.monthly_chats}</span></p>
                      <p>Searches: <span className="font-semibold text-slate-900">{p.monthly_searches}</span></p>
                      <p>Listings: <span className="font-semibold text-slate-900">{p.monthly_property_add}</span></p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <Badge className={`border shadow-none hover:bg-transparent text-xs ${p.is_active === 'true' ? 'border-emerald-200 text-emerald-700 bg-transparent' : 'border-red-200 text-red-700 bg-transparent'}`}>
                      {p.is_active === 'true' ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleToggleStatus(p)} title={p.is_active === 'true' ? "Deactivate" : "Activate"}>
                        {p.is_active === 'true' ? <PowerOff className="w-4 h-4 text-orange-500" /> : <Power className="w-4 h-4 text-emerald-500" />}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEditModal(p)} title="Edit">
                        <Pencil className="w-4 h-4 text-slate-500" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(p.id)} title="Delete">
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {plans.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-slate-500">No plans found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {editingPlan ? 'Edit Plan' : 'Create New Plan'}
              </h2>
              <button onClick={() => setIsModalOpen(false)} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4 max-h-[75vh] overflow-y-auto">
              <div>
                <Label className="block text-sm font-medium text-slate-700 mb-1.5">Plan Name <span className="text-red-500">*</span></Label>
                <Input required value={form.plan_name} onChange={e => setForm({...form, plan_name: e.target.value})} placeholder="e.g. Pro Plan" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="block text-sm font-medium text-slate-700 mb-1.5">Monthly Price (₹) <span className="text-red-500">*</span></Label>
                  <Input required type="number" step="0.01" value={form.monthly_amount} onChange={e => setForm({...form, monthly_amount: e.target.value})} placeholder="0.00" />
                </div>
                <div>
                  <Label className="block text-sm font-medium text-slate-700 mb-1.5">Yearly Price (₹) <span className="text-red-500">*</span></Label>
                  <Input required type="number" step="0.01" value={form.yearly_amount} onChange={e => setForm({...form, yearly_amount: e.target.value})} placeholder="0.00" />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="block text-sm font-medium text-slate-700 mb-1.5">Monthly Chats <span className="text-red-500">*</span></Label>
                  <Input required type="number" min="0" value={form.monthly_chats} onChange={e => setForm({...form, monthly_chats: e.target.value})} placeholder="0" />
                </div>
                <div>
                  <Label className="block text-sm font-medium text-slate-700 mb-1.5">Monthly Searches <span className="text-red-500">*</span></Label>
                  <Input required type="number" min="0" value={form.monthly_searches} onChange={e => setForm({...form, monthly_searches: e.target.value})} placeholder="0" />
                </div>
                <div>
                  <Label className="block text-sm font-medium text-slate-700 mb-1.5">Property Listings <span className="text-red-500">*</span></Label>
                  <Input required type="number" min="0" value={form.monthly_property_add} onChange={e => setForm({...form, monthly_property_add: e.target.value})} placeholder="0" />
                </div>
              </div>

              <Button type="submit" disabled={isSubmitting} className="w-full bg-slate-900 hover:bg-slate-800 text-white mt-4">
                {isSubmitting ? 'Saving...' : editingPlan ? 'Save Changes' : 'Create Plan'}
              </Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlansPage;
